import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import ChoosingStones from './instructions/ChoosingStones';
import PlacingStones from './instructions/PlacingStones';
import MovingStones from './instructions/MovingStones';
import PlacingWalls from './instructions/PlacingWalls';
import TimingOut from './instructions/TimingOut';
import GameOver from './instructions/GameOver';
import ScreenTransition from './ScreenTransition';
import StepTransition from './StepTransition';
import { STONES_PER_PLAYER, INITIAL_TIME } from '../constants';

const INSTRUCTIONS_STEPS = [
  {
    id: 'choosing-stones',
    title: '1. Choosing stones',
    description: `Each player gets ${STONES_PER_PLAYER} stones of one of the available colors, black or white. The color is determined at random.`,
    component: ChoosingStones
  },
  {
    id: 'placing-stones',
    title: '2. Placing stones',
    description: `Players take turns placing their stones on the board. Black moves first. Stones can be placed on any empty field.`,
    component: PlacingStones
  },
  {
    id: 'moving-stones',
    title: '3. Moving stones',
    description: 'After all stones are placed, players take turns moving their stones starting with black. A stone can move one or two steps in any direction vertically or horizontally, including backwards. The path must be clear of other stones and walls.',
    component: MovingStones
  },
  {
    id: 'placing-walls',
    title: '4. Placing walls',
    description: 'A wall must be placed around the moved stone. Walls can be placed horizontally or vertically, but cannot be placed where a wall already exists. Players cannot move through walls.',
    component: PlacingWalls
  },
  {
    id: 'timing-out',
    title: '5. Time limit',
    description: `Each player has ${INITIAL_TIME} seconds to make their move. A move is considered to be made in two cases. First, when stone is placed. Second, when a stone is moved and a wall is placed. If a player runs out of time, they lose the game.`,
    component: TimingOut
  },
  {
    id: 'game-over',
    title: '6. Game Over',
    description: 'The game ends when all stones of opposite colors are separated from each other with walls or when stones cannot move anymore. Players receive points for each field within the territory of their color. The player with the most points wins.',
    component: GameOver
  },
];

function Instructions() {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [transitionDirection, setTransitionDirection] = useState('next');
  const currentStep = INSTRUCTIONS_STEPS[currentStepIndex];
  const CurrentStepComponent = currentStep.component;

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setTransitionDirection('prev');
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentStepIndex < INSTRUCTIONS_STEPS.length - 1) {
      setTransitionDirection('next');
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const showPreviousButton = currentStepIndex > 0;
  const showNextButton = currentStepIndex < INSTRUCTIONS_STEPS.length - 1;

  return (
    <ScreenTransition direction="right">
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="flex flex-col items-center justify-between w-full space-y-4">
          {/* Back button */}
          <Button onClick={() => navigate('/')} className="w-64">
            Back
          </Button>

          {/* Main content */}
          <div className="flex sm:mx-8">
            <button
              onClick={handlePrevious}
              className={`
                  w-10 opacity-80 flex items-center justify-center rounded-xl bg-gray-800 hover:bg-gray-700 transition-colors
                  ${!showPreviousButton && 'invisible pointer-events-none'}
                `}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-yellow-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div className="bg-zinc-900/60 px-3 py-4 sm:p-8 rounded-2xl max-w-4xl overflow-hidden">
              <StepTransition direction={transitionDirection}>
                <div className="w-full">
                  <h2 className="text-lg sm:text-2xl font-bold text-center mb-2 sm:mb-6 text-yellow-300">
                    {currentStep.title}
                  </h2>
                  <div className="mb-4 sm:mb-8">
                    <p className="text-gray-200 text-sm sm:text-lg leading-relaxed text-center">
                      {currentStep.description}
                    </p>
                  </div>
                  <div className="border-gray-700 sm:border-t sm:pt-8">
                    <CurrentStepComponent />
                  </div>
                </div>
              </StepTransition>
            </div>

            <button
              onClick={handleNext}
              className={`
                  w-10 opacity-80 flex items-center justify-center rounded-xl bg-gray-800 hover:bg-gray-700 transition-colors
                  ${!showNextButton && 'invisible pointer-events-none'}
                `}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-yellow-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </ScreenTransition>
  );
}

export default Instructions; 
